<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { formatReportDuration } from 'dashboard/helper/reportDuration';

// SiteUP CRM (relatorios r2, item C): a media unica de primeira resposta mistura
// robo e pessoa, e quem le a tela entende desempenho da equipe. Aqui os dois
// aparecem lado a lado, com rotulo, a partir do recorte que o backend manda em
// `first_response_by_origin`. Lado sem nenhum evento mostra o marcador de vazio,
// nunca zero.
const props = defineProps({
  accountSummaryKey: {
    type: String,
    default: 'getAccountSummary',
  },
});

const { t } = useI18n();
const accountSummary = useMapGetter(props.accountSummaryKey);

const origin = computed(() => accountSummary.value?.first_response_by_origin);

const human = computed(() => origin.value?.human || {});
const bot = computed(() => origin.value?.bot || {});

const totalCount = computed(
  () => Number(human.value.count || 0) + Number(bot.value.count || 0)
);

const hasData = computed(() => Boolean(origin.value) && totalCount.value > 0);

const EMPTY_MARK = '·';

const formatSide = side =>
  Number(side.count || 0) > 0 && side.avg !== null && side.avg !== undefined
    ? formatReportDuration(side.avg)
    : EMPTY_MARK;

const humanValue = computed(() => formatSide(human.value));
const botValue = computed(() => formatSide(bot.value));

const share = count =>
  totalCount.value > 0
    ? Math.round((Number(count || 0) / totalCount.value) * 100)
    : 0;

const humanShare = computed(() => share(human.value.count));
const botShare = computed(() => share(bot.value.count));
</script>

<template>
  <div
    v-if="hasData"
    class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-n-slate-11"
    data-testid="first-response-origin"
  >
    <span class="inline-flex items-center gap-1">
      <i class="i-ph-user size-3.5 shrink-0" />
      {{ t('REPORT.FIRST_RESPONSE_ORIGIN.HUMAN') }}:
      <span class="font-semibold text-n-slate-12">{{ humanValue }}</span>
      <span>({{ human.count || 0 }} · {{ humanShare }}%)</span>
    </span>
    <span class="inline-flex items-center gap-1">
      <i class="i-ph-robot size-3.5 shrink-0" />
      {{ t('REPORT.FIRST_RESPONSE_ORIGIN.BOT') }}:
      <span class="font-semibold text-n-slate-12">{{ botValue }}</span>
      <span>({{ bot.count || 0 }} · {{ botShare }}%)</span>
    </span>
  </div>
</template>
